// Apolo IDE — useFileMentionSuggestions (app/hook, sprint composer-anchor E9).
//
// Dá as sugestões pro autocomplete de @file:/@folder: — detecta o token via core
// puro, lista o DIRETÓRIO atual via IPC `file.list` e filtra a folha
// client-side (sem refetch enquanto digita o nome). Sem bridge/root → inativo.

import { useEffect, useRef, useState } from 'react';
import { hasElectronBridge } from '../lib/electronBridge.js';
import { detectFileMentionPrefix, splitMentionPath } from '../lib/fileMention.js';

const MAX = 8;

/**
 * @param {{ input: string, root?: string }} params
 * @returns {{ detected: ReturnType<typeof detectFileMentionPrefix>, entries: Array, loading: boolean }}
 */
export function useFileMentionSuggestions({ input, root }) {
  const detected = typeof input === 'string' ? detectFileMentionPrefix(input) : null;
  const { dir, leaf } = detected ? splitMentionPath(detected.partial) : { dir: '', leaf: '' };
  const [listing, setListing] = useState({ dir: null, entries: [] });
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  const active = !!(detected && root && hasElectronBridge() && window.apolo?.file?.list);

  useEffect(() => {
    if (!active) return undefined;
    if (listing.dir === dir) return undefined; // já temos esse diretório listado
    let cancelled = false;
    const reqId = ++reqRef.current;
    setLoading(true);
    (async () => {
      let entries = [];
      try {
        const res = await window.apolo.file.list({ root, dir });
        if (Array.isArray(res)) entries = res;
      } catch {
        /* sem listagem → dropdown vazio, nunca quebra */
      }
      if (cancelled || reqId !== reqRef.current) return;
      setListing({ dir, entries });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [active, root, dir, listing.dir]);

  if (!active) return { detected: null, entries: [], loading: false };

  const base = listing.dir === dir ? listing.entries : [];
  const lower = leaf.toLowerCase();
  let entries = base.filter((e) => e && typeof e.name === 'string' && e.name.toLowerCase().includes(lower));
  if (detected.type === 'folder') entries = entries.filter((e) => e.isDirectory);
  entries = [...entries].sort((a, b) => {
    const ap = a.name.toLowerCase().startsWith(lower) ? 0 : 1;
    const bp = b.name.toLowerCase().startsWith(lower) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return { detected, entries: entries.slice(0, MAX), loading: loading && listing.dir !== dir };
}
